'use client';

import * as React from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { AnalyticsProvider as AnalyticsProviderImpl } from '../provider';
import type { AnalyticsProvider as AnalyticsProviderInterface } from '../provider/types';

const COOKIE_CONSENT_UPDATED_EVENT = 'cookie-consent-updated';

function getCurrentUrl(
  pathname: string,
  searchParams: ReturnType<typeof useSearchParams>
): string {
  return [pathname, searchParams.toString()].filter(Boolean).join('?');
}

function useTrackPageView(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  React.useEffect(() => {
    const url = getCurrentUrl(pathname, searchParams);
    AnalyticsProviderImpl.trackPageView(url);
  }, [pathname, searchParams]);

  React.useEffect(() => {
    const handleConsentUpdate = (event: Event): void => {
      const consent = (event as CustomEvent<string>).detail;
      if (consent !== 'accepted') {
        return;
      }

      const url = getCurrentUrl(pathname, searchParams);
      AnalyticsProviderImpl.trackPageView(url);
    };

    window.addEventListener(
      COOKIE_CONSENT_UPDATED_EVENT,
      handleConsentUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        COOKIE_CONSENT_UPDATED_EVENT,
        handleConsentUpdate as EventListener
      );
    };
  }, [pathname, searchParams]);
}

export const AnalyticsContext = React.createContext<AnalyticsProviderInterface>(
  AnalyticsProviderImpl
);

export function AnalyticsProvider(props: React.PropsWithChildren) {
  if (typeof window === 'undefined') {
    return props.children;
  }
  useTrackPageView();
  return (
    <AnalyticsContext.Provider value={AnalyticsProviderImpl}>
      {props.children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsProviderInterface {
  return React.useContext(AnalyticsContext);
}
